/**
 * TenantEntry - the `/t/{slug}` entry point a tenant's users bookmark.
 *
 * Establishes which tenant a sign-in is for *before* authenticating, so the IdP knows which upstream
 * directory to delegate to (ADR 024 decision 6). A path segment rather than a hostname, so adding a tenant
 * costs no DNS record, no certificate and no Ingress change.
 *
 * The slug is a hint, never an authorization decision: the IdP resolves it against the signing-in user's
 * own memberships and refuses a tenant they do not belong to.
 */
import { useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { PageShell } from '@/components/common/PageShell'

export function TenantEntry() {
  const { slug } = useParams<{ slug: string }>()
  const { isInitialized, isAuthenticated, loginToTenant } = useAuth()
  const started = useRef(false)

  useEffect(() => {
    // Guarded: a redirect is a one-way trip, and React's development double-invoke would otherwise start
    // two of them and leave the second overwriting the first's PKCE state.
    if (!isInitialized || isAuthenticated || started.current || !slug) {
      return
    }
    started.current = true
    loginToTenant(slug, '#/')
  }, [isInitialized, isAuthenticated, slug, loginToTenant])

  return (
    <PageShell>
      <div className="flex items-center justify-center min-h-[60vh] p-6">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
          <p className="text-muted-foreground">Signing in to {slug}…</p>
        </div>
      </div>
    </PageShell>
  )
}
